'use client'

import { useEffect } from 'react'

/**
 * A tab open across a deploy is running code that no longer exists.
 *
 * Next splits the app into hashed chunks and loads them on demand. After a
 * deploy the old chunks are gone from the server, so the first navigation that
 * needs one fails — and what the user sees is a blank screen or a red overlay
 * about a "module factory", which tells them nothing they can act on. The
 * clinic's screens stay open all day, so this lands on staff mid-shift.
 *
 * The page reloads itself instead. Reloading is the whole fix: the service
 * worker serves documents network-first, so a reload fetches the new HTML,
 * which points at chunks that exist. Static assets stay cache-first, which is
 * safe because their names carry a hash.
 *
 * Once, though. If the reload does not fix it — the network is down, a proxy is
 * serving something stale — a second one would loop the tab forever and hide
 * the real error. After the cooldown the failure is left to surface.
 */

const LAST_RELOAD_KEY = 'chunk-reload-at'
const COOLDOWN_MS = 30_000

/**
 * Does this failure mean "the code this tab is running is gone"?
 *
 * The wording differs by bundler and browser, and none of them expose a code —
 * webpack throws ChunkLoadError, Turbopack talks about a missing module
 * factory, Safari and Firefox each phrase the dynamic-import failure their own
 * way. Matching text is unpleasant but it is what there is, so the list is
 * explicit and every entry is a message actually seen in the wild.
 */
export function looksLikeStaleBundle(message?: string | null, name?: string | null): boolean {
  if (name === 'ChunkLoadError') return true

  const m = (message || '').toLowerCase()
  if (!m) return false

  return (
    m.includes('loading chunk') ||                              // webpack
    m.includes('loading css chunk') ||
    m.includes('module factory is not available') ||            // turbopack
    m.includes('failed to fetch dynamically imported module') || // chrome
    m.includes('error loading dynamically imported module') ||   // firefox
    m.includes('importing a module script failed')               // safari
  )
}

export function ChunkReloadGuard() {
  useEffect(() => {
    const reloadOnce = (why: string) => {
      let last = 0
      try {
        last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0)
      } catch {
        // Private mode or storage disabled: without a memory of having tried,
        // reloading could loop. Do nothing and let the error show.
        return
      }
      if (Date.now() - last < COOLDOWN_MS) {
        console.warn(`[ChunkReloadGuard] still failing after a reload (${why}) — leaving it alone`)
        return
      }
      sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
      console.warn(`[ChunkReloadGuard] stale bundle (${why}) — reloading`)
      window.location.reload()
    }

    // A dynamic import that rejects: the usual shape on navigation.
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason as { message?: string; name?: string } | undefined
      if (looksLikeStaleBundle(reason?.message, reason?.name)) reloadOnce('import rejected')
    }

    // A thrown error carrying the same message.
    const onError = (e: ErrorEvent) => {
      if (looksLikeStaleBundle(e.message, (e.error as Error | undefined)?.name)) reloadOnce('error thrown')
    }

    // A <script> or <link> that 404s. These do not bubble, hence capture, and
    // they carry no message — the giveaway is the URL.
    const onResourceError = (e: Event) => {
      const el = e.target as (HTMLScriptElement & HTMLLinkElement) | null
      const url = el?.src || el?.href
      if (typeof url === 'string' && url.includes('/_next/static/')) reloadOnce('asset missing')
    }

    window.addEventListener('unhandledrejection', onRejection)
    window.addEventListener('error', onError)
    window.addEventListener('error', onResourceError, true)
    return () => {
      window.removeEventListener('unhandledrejection', onRejection)
      window.removeEventListener('error', onError)
      window.removeEventListener('error', onResourceError, true)
    }
  }, [])

  return null
}
